import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import {
  Plus,
  Search,
  FileDown,
  ChevronDown,
  Star,
  Calendar,
} from "lucide-react";

interface LegalDocumentItem {
  id: string;
  stt: number;
  outgoingNumber: string;
  documentSymbol: string;
  issueDate: string;
  issuingAuthority: string;
  contentSummary: string;
  receivedDate: string;
  priority: string;
}

const mockData: LegalDocumentItem[] = [
  {
    id: "1",
    stt: 1,
    outgoingNumber: "0",
    documentSymbol: "NĐ-2025-001",
    issueDate: "08/2025",
    issuingAuthority: "Chính phủ",
    contentSummary: "Nghị định về quản lý văn bản pháp quy",
    receivedDate: "29/08/2025",
    priority: "urgent",
  },
  {
    id: "2",
    stt: 2,
    outgoingNumber: "0",
    documentSymbol: "TT-2025-002",
    issueDate: "08/2025",
    issuingAuthority: "Bộ Tư pháp",
    contentSummary: "Thông tư hướng dẫn thực hiện nghị định",
    receivedDate: "28/08/2025",
    priority: "normal",
  },
  {
    id: "3",
    stt: 3,
    outgoingNumber: "0",
    documentSymbol: "QĐ-2025-003",
    issueDate: "08/2025",
    issuingAuthority: "Thủ tướng Chính phủ",
    contentSummary: "Quyết định ban hành quy chế quản lý",
    receivedDate: "27/08/2025",
    priority: "secret",
  },
  {
    id: "4",
    stt: 4,
    outgoingNumber: "0",
    documentSymbol: "CV-2025-004",
    issueDate: "08/2025",
    issuingAuthority: "Bộ Nội vụ",
    contentSummary: "Công văn hướng dẫn thực hiện",
    receivedDate: "26/08/2025",
    priority: "high",
  },
];

const LegalDocuments: React.FC = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case "normal":
        return <Star className="h-4 w-4 text-gray-300" />;
      case "high":
        return <Star className="h-4 w-4 text-red-500" />;
      case "urgent":
        return <Star className="h-4 w-4 text-red-500 fill-current" />;
      case "secret":
        return <Star className="h-4 w-4 text-blue-300" />;
      case "top_secret":
        return <Star className="h-4 w-4 text-blue-500" />;
      case "confidential":
        return <Star className="h-4 w-4 text-blue-600 fill-current" />;
      default:
        return <Star className="h-4 w-4 text-gray-300" />;
    }
  };

  return (
    <PageShell title="Văn bản pháp quy">
      <div
        className="space-y-6 [&>*]:space-y-0 [&>*]:!mt-0"
        style={
          { "--tw-space-y-reverse": "0 !important" } as React.CSSProperties
        }
      >
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Văn bản pháp quy
          </h1>
          <button
            onClick={() => navigate("/add-new-document")}
            className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Thêm mới Văn bản</span>
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm văn bản pháp quy"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - All filters in one row */}
            <div className="flex items-end space-x-2">
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white w-36">
                <option>- Loại văn bản -</option>
                <option>Nghị định</option>
                <option>Thông tư</option>
                <option>Quyết định</option>
                <option>Công văn</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white w-36">
                <option>- Cơ quan ban hành -</option>
                <option>Chính phủ</option>
                <option>Bộ Tư pháp</option>
                <option>Bộ Nội vụ</option>
                <option>Thủ tướng Chính phủ</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white w-36">
                <option>- Ưu tiên -</option>
                <option>Thường</option>
                <option>Gấp</option>
                <option>Hỏa tốc</option>
                <option>Mật</option>
              </select>

              {/* Ngày ban hành */}
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">
                  Ngày ban hành
                </label>
                <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                  <input
                    type="text"
                    placeholder="mm/dd/yyyy"
                    className="px-2 py-2 text-sm border-none focus:ring-0 focus:outline-none w-32"
                  />
                  <button
                    type="button"
                    className="px-2 py-2 text-gray-600 hover:bg-gray-100"
                  >
                    <Calendar className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Ngày nhận */}
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">Ngày nhận</label>
                <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                  <input
                    type="text"
                    placeholder="mm/dd/yyyy"
                    className="px-2 py-2 text-sm border-none focus:ring-0 focus:outline-none w-32"
                  />
                  <button
                    type="button"
                    className="px-2 py-2 text-gray-600 hover:bg-gray-100"
                  >
                    <Calendar className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Row 3 - Export button */}
            <div className="flex justify-start">
              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>
        </div>

        {/* Priority Indicators */}
        <div className="bg-white rounded-lg p-2 -mt-6">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <Star className="h-4 w-4 text-gray-300" />
              <span className="text-sm text-gray-700">Thường</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="h-4 w-4 text-red-500" />
              <span className="text-sm text-gray-700">Gấp</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="h-4 w-4 text-red-500 fill-current" />
              <span className="text-sm text-gray-700">Hỏa tốc</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="h-4 w-4 text-blue-300" />
              <span className="text-sm text-gray-700">Mật</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="h-4 w-4 text-blue-500" />
              <span className="text-sm text-gray-700">Tuyệt Mật</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="h-4 w-4 text-blue-600 fill-current" />
              <span className="text-sm text-gray-700">Tối mật</span>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-8">
                    STT
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Số đến
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-28">
                    Số ký hiệu văn bản
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24">
                    Ngày ban hành
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Cơ quan ban hành
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Trích yếu nội dung
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-24">
                    Ngày nhận
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-8">
                      {item.stt}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      <div className="flex items-center space-x-1">
                        {getPriorityIcon(item.priority)}
                        <span className="text-gray-700 font-medium">
                          {item.outgoingNumber}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-28">
                      {item.documentSymbol || (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-24">
                      {item.issueDate}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32">
                      {item.issuingAuthority}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32">
                      <div className="font-medium text-[#337ab7] text-[14px] leading-tight">
                        {item.contentSummary}
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 w-24">
                      {item.receivedDate}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">10000</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                2
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                3
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default LegalDocuments;
